/**
 * AdhviBooks Design Tokens
 * ========================
 * THIS is the single source of truth for ALL visual design decisions.
 *
 * To change a font:   edit `fonts` below — done globally in 1 line.
 * To change a color:  edit `colors` below — done globally in 1 line.
 * To change spacing:  edit `spacing` below.
 *
 * These tokens feed:
 *   1. tailwind.config.ts  → Tailwind utility classes
 *   2. src/theme/css.ts    → CSS custom properties (injected into :root)
 *   3. src/theme/index.ts  → typed JS/TS imports for inline styles when needed
 *
 * NEVER hardcode a color, font, or spacing value in a component.
 * ALWAYS use a token, a Tailwind class, or a CSS variable.
 */

// ─── FONTS ───────────────────────────────────────────────────────────────────
// To swap fonts: change the values below. Everything else updates automatically.
export const fonts = {
  display: "'Sora', 'Segoe UI', sans-serif",   // headings, logo, hero text
  body:    "'Nunito', 'Segoe UI', sans-serif",  // body copy, buttons, labels
  mono:    "'Fira Code', 'Courier New', monospace", // code snippets
} as const;

export type FontKey = keyof typeof fonts;

// Google Fonts URL — generated from the font names above.
// Update this when you change fonts.
export const googleFontsUrl =
  "https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800;900&family=Sora:wght@600;700;800&family=Fira+Code:wght@400;500&display=swap";

// ─── COLORS ──────────────────────────────────────────────────────────────────
export const colors = {
  // Brand — the core palette
  primary:   "#534AB7", // purple — buttons, links, active states
  primary700:"#3C3489", // darker purple — hover states
  primary50: "#EEEDFE", // lightest purple — subtle backgrounds
  accent:    "#1D9E75", // teal — success, highlights, CTAs
  accent50:  "#E1F5EE", // lightest teal
  dark:      "#26215C", // deep navy — hero bg, bold headings

  // Neutral
  text:      "#1A1730", // near-black — primary text
  textSub:   "#9B97C8", // muted — secondary text, placeholders
  textMuted: "#B4B2A9", // lighter — metadata, hints
  border:    "#EAE8F8", // subtle border
  borderMid: "#CECBF6", // mid border — hover, focus
  surface:   "#FAFAFA", // card/input background
  surfaceAlt:"#F4F2FF", // tinted purple surface

  // Semantic
  success:   "#1D9E75",
  success50: "#E1F5EE",
  warning:   "#BA7517",
  warning50: "#FAEEDA",
  error:     "#E24B4A",
  error50:   "#FCEBEB",
  info:      "#185FA5",
  info50:    "#E6F1FB",

  // Category chips — book genres
  category: {
    fantasy:   { bg: "#EDE9FE", text: "#4C1D95" },
    adventure: { bg: "#D1FAE5", text: "#065F46" },
    science:   { bg: "#FEF3C7", text: "#92400E" },
    stories:   { bg: "#FCE7F3", text: "#831843" },
    comics:    { bg: "#FFEDD5", text: "#9A3412" },
    school:    { bg: "#DBEAFE", text: "#1E3A8A" },
    nature:    { bg: "#D1FAE5", text: "#065F46" },
    other:     { bg: "#F4F2FF", text: "#534AB7" },
  },
} as const;

export type ColorKey = keyof typeof colors;
export type CategoryKey = keyof typeof colors.category;

// ─── SPACING ─────────────────────────────────────────────────────────────────
// Based on a 4px grid. Use these values — don't invent new ones.
export const spacing = {
  "0":  "0px",
  "1":  "4px",
  "2":  "8px",
  "3":  "12px",
  "4":  "16px",
  "5":  "20px",
  "6":  "24px",
  "8":  "32px",
  "10": "40px",
  "12": "48px",
  "16": "64px",
} as const;

// ─── BORDER RADIUS ───────────────────────────────────────────────────────────
export const radii = {
  sm:   "6px",
  md:   "10px",
  lg:   "14px",
  xl:   "18px",
  "2xl":"22px",
  full: "9999px",
} as const;

export type RadiusKey = keyof typeof radii;

// ─── SHADOWS ─────────────────────────────────────────────────────────────────
export const shadows = {
  sm:  "0 1px 3px rgba(0,0,0,0.04)",
  md:  "0 4px 14px rgba(83,74,183,0.09)",
  lg:  "0 8px 24px rgba(83,74,183,0.12)",
  xl:  "0 20px 60px rgba(83,74,183,0.14)",
  btn: "0 4px 18px rgba(83,74,183,0.28)",
} as const;

// ─── TRANSITIONS ─────────────────────────────────────────────────────────────
export const transitions = {
  fast:   "all 0.13s ease",
  normal: "all 0.18s ease",
  slow:   "all 0.28s ease",
} as const;

// ─── Z-INDEX ─────────────────────────────────────────────────────────────────
export const zIndex = {
  base:    0,
  raised:  10,
  nav:     100,
  modal:   1000,
  tooltip: 2000,
} as const;

// ─── BREAKPOINTS ─────────────────────────────────────────────────────────────
export const breakpoints = {
  sm:  "640px",
  md:  "768px",
  lg:  "1024px",
  xl:  "1280px",
} as const;

// ─── ANIMATION KEYFRAMES ─────────────────────────────────────────────────────
// Kept here so CSS and component code reference the same names.
export const animationNames = {
  fadeIn:   "adhvi-fadeIn",
  slideUp:  "adhvi-slideUp",
  slideIn:  "adhvi-slideIn",
  popIn:    "adhvi-popIn",
  bounce:   "adhvi-bounce",
  spin:     "adhvi-spin",
  checkPop: "adhvi-checkPop",
  pulse:    "adhvi-pulse",
} as const;

// ─── FULL THEME OBJECT ───────────────────────────────────────────────────────
// Import this anywhere you need multiple tokens at once.
export const theme = {
  fonts,
  colors,
  spacing,
  radii,
  shadows,
  transitions,
  zIndex,
  breakpoints,
  animationNames,
} as const;

export type Theme = typeof theme;
